import React, { useMemo, useContext } from "react";
import SwitcherWithVariants from "components/switcher-with-variants";
import Switcher from "components/switcher";
import SwitcherWithImages from "components/switcher-with-images";
import ColorSwatchesCategory from "components/color-swatch-category";
import VariantCategoryLevel from "components/variant-category-level";
import VariantList from "components/variant-list";
import KnifeVariants from "components/knife-variants";
import ColorSwatch from "components/color-swatch";
import Accessoires from "components/accessoires-template";
import ProductContext from "services/product-context";
import VariantCategoryDetails from "components/variant-category-details";
import * as styles from "./style.mod.scss";
import Link from "components/link";
import locale from "locales";

const filterEmptyCategories = (hierarchy) => hierarchy.filter((item) => item.children.find((cat) => cat._type === "productVariant") !== undefined || filterEmptyCategories(item.children).length !== 0);
const getHierarchy = (variantCategories, allCategories, variants) => {
  if (variantCategories.length === 0) return [];

  return variantCategories.map((vc) => {
    const childCategories = getHierarchy(allCategories.filter((cat) => cat.parentCategory?._ref === vc._id), allCategories, variants).map((cat) => ({
      ...cat, parent: vc
    }));

    return {
      ...vc,
      children: childCategories.length === 0 ? variants.filter((variant) => variant.variantCategory?._ref === vc._id) : childCategories
    };
  });
};

const getParents = (category, allCategories) => {
  if (!category.parentCategory) return [category];
  const parent = allCategories.find((cat) => cat._id === category.parentCategory._ref);

  return [category, parent, ...getParents(parent, allCategories)].flat();
};

const getSelectedNodes = (variantCategories, currentVariant) => {
  const { variantCategory } = currentVariant;

  if (!variantCategory) return [];

  const matchingCategory = variantCategories.find((vc) => vc._id === variantCategory._ref);

  return getParents(matchingCategory, variantCategories).map((vc) => vc._id);
};

const getVariantCategories = (variantCategories, selected) => variantCategories.map((cat) => ({
  ...cat, selected: selected.indexOf(cat._id) !== -1
}));


const ProductVariants = ({ currentVariant, product, props }) => {
  const { variants, variantCategories, content } = useContext(ProductContext);

  const selectedCategoryIds = useMemo(() => getSelectedNodes(variantCategories, currentVariant), [variantCategories, currentVariant]);
  const hierarchy = useMemo(() => {

    const mappedCategories = getVariantCategories(variantCategories, selectedCategoryIds);
    const rootCategories = mappedCategories.filter((cat) => !cat.parentCategory);

    return filterEmptyCategories(getHierarchy(rootCategories, mappedCategories, variants));
  }, [variantCategories, currentVariant]);
  const visibleVariants = variants.filter((variant) => selectedCategoryIds.indexOf(variant.variantCategory?._ref) !== -1);

  return (
    <>
      <SwitcherWithVariants content={content?.switcherVariants} product={product} />
      <Switcher content={content?.switcherNewProducts} />
      {content?.helpMeSection &&
        <VariantCategoryDetails content={content?.helpMeSection} />}
      <SwitcherWithImages content={content?.productWthImageLink} />
      {content?.pdpTemplate === "categoryColorSwatches" ?
        <ColorSwatchesCategory
          variants={variants}
          currentVariant={currentVariant}
          hierarchy={hierarchy}
        />
        :
        <>
          {content?.switcherVariants == null &&
            <div className={styles.advancedProductGroup}>
              {props?.linkedProducts &&
                <div className={styles.parentGroup}>
                  <ul>
                    {props.linkedProducts.map((data, index) => <Link
                      key={index}
                      partiallyActive={true}
                      activeClassName={styles.selected}
                      to={`/products${data.url}`}
                      state={{ preserveScroll: true }}>
                      {data.name}{data?.badge && <span className={styles.badge}> {locale.products.new}</span>}
                    </Link>)
                    }
                  </ul>
                </div>
              }
              <VariantCategoryLevel
                categories={hierarchy}
                variants={variants} />

              {content?.pdpTemplate === "default" && <VariantList
                variants={visibleVariants?.map((variant) => ({
                  id: variant?.id, image: variant?.variantIcon, handle: variant?.handle?.current, title: variant.variantTitle, showTitle: variant.showTitle
                }))}
                isSelected={(variant) => variant.id === currentVariant.id}
              />}
              {content?.pdpTemplate === "knife" && <KnifeVariants
                variants={visibleVariants}
                currentVariant={currentVariant} />}
              {content?.pdpTemplate === "apron" && <ColorSwatch
                variants={variants}
                currentVariant={currentVariant} />}
              {content?.pdpTemplate === "accessoires" &&
                <Accessoires
                  variants={variants}
                  currentVariant={currentVariant}
                  content={content} />
              }

            </div>}
        </>}</>
  );
};

export default ProductVariants;